import React, { useRef, useMemo, useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'src/components/Highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getChartOptions } from './components';
import moment from 'moment';
import _isEmpty from 'lodash/isEmpty';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { Option } from 'funfix-core';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { useAppSelector } from 'src/utils/Hooks';
import { computeDataSet, formatLegendData, buildChartSeries, mapStateToProps } from './utils';
import { getEntityDisplayNames } from '../../Ineligible/utils';
import colors from 'src/utils/colors';
import { StackedColumnChartLoading } from 'src/components/common/Loading/PlaceHolderLoading/PlaceHolderLoading';
import StackedColumnLegend from './StackedColumnLegendV2';
import { WidgetProps } from 'src/types/application/widgetTypes';
import { getTotalMonthlyBudgetForCurrentEntity } from 'src/components/AdManager/budgetCalculation';
import { useInView } from 'react-intersection-observer';
import { generateBudgetPacingCSV, fetchDailySpendAndProjections } from '../utils';
import { getSearchAppName } from 'src/utils/app';
import { useEntityPhrasing } from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { styled } from '@mui/material/styles';
import Export from 'src/components/Export/ExportCSV';
import { saveAs } from 'file-saver';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

const DailySpendBarChartWrapper = styled('div')({
  backgroundColor: '#fff',
  '.trend-bar-chart': {
    marginBottom: '40px !important'
  },
  '.section_headerline': {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between'
  },
  '.barchart__highcharts': {
    marginTop: '32px'
  },
  '.section_subHeaderline': {
    borderBottom: 'none !important'
  },
  '.ad__header': {
    width: '100%',
    marginBottom: '25px',
    '.header__left': {
      display: 'flex',
      flexDirection: 'column'
    }
  },

  '.legend-item': {
    width: '248px !important',
    '.title': {
      textTransform: 'uppercase',
      color: `${colors.grey} !important`,
      fontSize: '12px !important',
      fontWeight: '500 !important',
      whiteSpace: 'nowrap',
      letterSpacing: '0.96px'
    },
    '.value': {
      fontSize: '26px !important',
      fontWeight: '700 !important'
    },
    '.secondary-value': {
      fontSize: '12px !important',
      fontWeight: '500 !important'
    }
  }
});

export const DailySpendBarChartInner: React.FC<
  Pick<WidgetProps, 'queryConditions'> & ReturnType<typeof mapStateToProps>
> = ({ mainEntity, app, retailer, entitySearchService, queryConditions, adCampaigns, adPortfolios, adEntities }) => {
  let entityPhrase = useEntityPhrasing({ titleOnly: true });
  const { ref, inView } = useInView({ threshold: 0 });
  const [dataFetched, setDataFetched] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const saveImageRef = useRef<HTMLDivElement>(null);
  const adLoading = useAppSelector((state) => state.adManagerLoading);

  const baseDataKey = `dailyAdSpendBarChart-{mainEntity!.id}`;
  useEffect(() => {
    if (!mainEntity || !inView || dataFetched || _isEmpty(adCampaigns)) {
      return;
    }
    setDataLoading(true);
    fetchDailySpendAndProjections(
      baseDataKey,
      { mainEntity, retailer, app: { ...app, name: getSearchAppName() } },
      queryConditions
    ).finally(() => {
      setDataLoading(false);
      setDataFetched(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDataKey, adCampaigns, mainEntity, inView, dataFetched, setDataLoading]);

  const { [`${baseDataKey}-projections`]: adSpendMetricsProjected, [`${baseDataKey}-actual`]: adSpendMetricsActual } =
    entitySearchService;

  const computed = useMemo(() => {
    if (
      !adSpendMetricsProjected ||
      !adSpendMetricsActual ||
      !mainEntity ||
      !adSpendMetricsActual.spend_by_dayId ||
      !adSpendMetricsProjected.spend_by_dayId
    ) {
      return null;
    }

    let maxDayIdWithActual = 0;
    if (adSpendMetricsActual.spend_by_dayId) {
      adSpendMetricsActual.spend_by_dayId.data.forEach((datum) => {
        const dayId = +datum.name!;
        if (dayId > maxDayIdWithActual) {
          maxDayIdWithActual = dayId;
        }
      });
    }

    let totalMonthlyBudget = getTotalMonthlyBudgetForCurrentEntity(mainEntity, adCampaigns, adPortfolios, adEntities);
    const averageDailyBudget = totalMonthlyBudget / moment().daysInMonth();

    // Zip up the fetched projected + actual ad spend metrics into a mapping of dayId to spend for that week
    const projectedAdSpendByDayData: {
      dayId: number;
      budgetAmount: number;
      value: number;
      isProjected: boolean;
    }[] = [];
    adSpendMetricsProjected.spend_by_dayId.data.forEach((datum) => {
      if (+datum.name! > maxDayIdWithActual) {
        projectedAdSpendByDayData.push({
          ...datum,
          dayId: +datum.name!,
          budgetAmount: datum.value || 0, // for projection spend is equal to budget, since our algorithm will set the budget based on projection
          isProjected: true,
          value: datum.value || 0
        });
      }
    });
    const actualAdSpendByDayData: {
      dayId: number;
      budgetAmount: number;
      value: number;
      isProjected: boolean;
    }[] = [];
    if (adSpendMetricsActual.spend_by_dayId) {
      adSpendMetricsActual.spend_by_dayId.data.forEach((datum, index) => {
        actualAdSpendByDayData.push({
          ...datum,
          dayId: +datum.name!,
          budgetAmount:
            mainEntity.type === 'adCampaign'
              ? adSpendMetricsActual.budgetAmount_by_dayId.data[index].value
              : averageDailyBudget, // only for campaigns take daily budget into account. For portfolio and entity use the average daily budget based on monthly budget
          isProjected: false,
          value: datum.value || 0
        });
      });
    }

    const metricsByDayId: Map<number, { actual?: number; projected?: number; budgetAmount: number }> = [
      ...projectedAdSpendByDayData,
      ...actualAdSpendByDayData
    ].reduce((acc, { dayId, value, isProjected, budgetAmount }) => {
      const entry = acc.get(+dayId) || {};
      if (isProjected) {
        entry.projected = value;
      } else {
        entry.actual = value;
      }
      entry.budgetAmount = budgetAmount;
      acc.set(+dayId, entry);
      return acc;
    }, new Map());
    const dataSet = computeDataSet(metricsByDayId);

    if (mainEntity.type === 'adCampaign') {
      totalMonthlyBudget = dataSet.totalCampaignBudgetSum;
    }
    return { totalMonthlyBudget, dailyBudgets: dataSet.dailyBudget, dataSet };
  }, [adSpendMetricsProjected, adSpendMetricsActual, mainEntity, adCampaigns, adPortfolios, adEntities]);

  const chartOptions = useMemo(() => {
    const temp = Option.of(computed)
      .map(({ dailyBudgets }) => ({ dailyBudgets }))
      .map(getChartOptions)
      .orNull();
    return temp;
  }, [computed]);
  const retailerSku = useAppSelector((state) => state?.entityService?.mainEntity?.retailerSku);

  const entityType = useMainEntityType();
  if (entityType === 'product') {
    entityPhrase = '';
  }

  const { dataSet, totalMonthlyBudget } = computed || {};
  const adjustedEntityPhrase = entityType === 'adCampaign' ? 'this campaign' : `the ${entityPhrase}`;
  const adjustedEntityTypeSuffix =
    entityType === 'adCampaign' ? '' : getEntityDisplayNames(entityType, retailerSku).toLowerCase();

  const renderHeader = useCallback(() => {
    return (
      <div className="ad__header">
        <div className="section_headerline" ref={ref}>
          Budget Pacing for {moment().format('MMMM')}
          <Export
            saveImageRef={saveImageRef}
            convertSeriesToDelimitedData={async () => {
              const csv = generateBudgetPacingCSV(dataSet);
              const blob = new Blob([csv], { type: 'text/csv' });
              saveAs(blob, 'daily_spend_data.csv');
            }}
          />
        </div>
        <div className="section_subHeaderline">
          <Text variant="body1">
            The chart displays the daily budget pacing for {adjustedEntityPhrase} {adjustedEntityTypeSuffix} for the
            month of {moment().format('MMMM')}.
          </Text>
        </div>
      </div>
    );
  }, [ref, adjustedEntityPhrase, adjustedEntityTypeSuffix, dataSet]);

  const mainLegendData = useMemo(() => {
    if (!computed || !chartOptions) {
      return null;
    }
    const { mainLegendData: mainLegendDataResult } = formatLegendData(dataSet, totalMonthlyBudget);
    return mainLegendDataResult;
  }, [dataSet, totalMonthlyBudget, computed, chartOptions]);
  if (adLoading || dataLoading) {
    return (
      <DailySpendBarChartWrapper>
        {renderHeader()}
        <br />
        <br />
        <StackedColumnChartLoading />
      </DailySpendBarChartWrapper>
    );
  }
  const series = dataSet ? buildChartSeries(dataSet) : [];
  return (
    <DailySpendBarChartWrapper ref={saveImageRef}>
      {renderHeader()}
      {_isEmpty(series) ? (
        <NoDataPlaceHolder />
      ) : (
        <>
          <StackedColumnLegend mainLegendData={mainLegendData} />
          <div style={{ position: 'relative' }} className="barchart__highcharts">
            <HighchartsReact highcharts={Highcharts} options={{ ...chartOptions, series }} />
          </div>
        </>
      )}
    </DailySpendBarChartWrapper>
  );
};

export default connect(mapStateToProps)(DailySpendBarChartInner) as React.ComponentType<
  Pick<WidgetProps, 'queryConditions'> & Partial<ReturnType<typeof mapStateToProps>>
>;
